import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Flex, Grid } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { FeatureFlags } from '../../../../../../../../../../components/Authorizer';
import useCompanyFeatures from '../../../../../../../../../../components/Authorizer/useCompanyFeatures';
import { CypressIds } from '../../../../../../../../../../constants/cypressIds';
import DataTypes from '../../../../../../../../../../constants/dataTypes';
import { FeatureTypes } from '../../../../../../../../../../constants/featureTypes';
import { PolicyTypes } from '../../../../../../../../../../constants/policyTypes';
import { selectCompanyFeatures } from '../../../../../../../../../../redux/modules/company';
import { selectPolicyLinkedItems } from '../../../../../../../../../../redux/modules/insured';
import { useRecordQuestionRequirements } from '../../../../../../../../../../services/record-service';
import { get } from '../../../../../../../../../../utils/arrayUtil';
import QuestionRequirementIconButton from '../../../QuestionRequirementIconButton';

import AiScanIcon from './components/AiScanIcon';
import PolicyDetailsGrid from './components/PolicyDetailsGrid';
import PolicyItemsPopover from './components/PolicyItemsPopover';
import PolicyLimitsGrid from './components/PolicyLimitsGrid';
import PolicyLimitsOptionsGrid from './components/PolicyLimitsOptionsGrid';
import PolicyNameGrid from './components/PolicyNameGrid';
import StatusOptions from './components/StatusOptions';

const PolicyTable = ({
  companyId,
  recordId,
  insuredId,
  jobId,
  matchedPoliciesAndRequirements = []
}) => {
  const alternatingColor = ['', 'greyScale.100'];
  const linkedItems = useSelector(selectPolicyLinkedItems);
  const { companyHasFeature: aiScanEnabled } = useCompanyFeatures(FeatureTypes.AiTierOne);
  const itemsFeature = useCompanyFeatures(FeatureFlags.Items);

  const templateColumns = `
    minmax(0, 2Fr)
    minmax(0, 3Fr)
    minmax(0, 3Fr)
    minmax(0, 4Fr)
    minmax(0, 2Fr)`;

  const conditionalTemplateColumns = (itemsFeature.companyHasFeature || aiScanEnabled)
    ? `minmax(25px, 25px) ${templateColumns}`
    : templateColumns;

  if (!matchedPoliciesAndRequirements?.length) {
    return (
      <Box bg="white" marginTop={5}>
        <Flex
          alignItems="center"
          color="greyScale.700"
          height="200px"
          justifyContent="center"
        >
          No Policy Requirements
        </Flex>
      </Box>
    );
  }

  return (
    <Box bg="white" marginTop={5}>
      <Box maxH={500} overflowX="auto" textStyle="bodyMd">
        {matchedPoliciesAndRequirements.map((policy, idx) => {
          const {
            limits,
            booleanProperties,
            alphaNumericProperties
          } = policy.propertyValues.reduce((allValues, propertyValue) => {
            const { dataType } = propertyValue.insurancePolicyTypeProperty;

            switch (dataType) {
              case (DataTypes.Monetary):
                allValues.limits.push(propertyValue);
                break;
              case (DataTypes.Boolean):
                allValues.booleanProperties.push(propertyValue);
                break;
              case (DataTypes.String):
                allValues.alphaNumericProperties.push(propertyValue);
                break;
              default:
                break;
            }

            return allValues;
          }, { limits: [], booleanProperties: [], alphaNumericProperties: [] });

          return (
            <Grid
              bg={alternatingColor[idx % alternatingColor.length]}
              data-cy={CypressIds.policyTypeRow(policy.insurancePolicyType.id, policy?.id)}
              gap={8}
              key={`${policy.id ?? `${policy.insurancePolicyType.id}-${policy.insurancePolicyType.displayName}`}`}
              minW="1245px"
              mr={0}
              p="15px"
              templateColumns={conditionalTemplateColumns}
              templateRows="repeat(1, 1fr)"
            >
              {itemsFeature.companyHasFeature && (
                <PolicyItemsPopover
                  companyId={companyId}
                  data={get(linkedItems, `${policy.id}.data`)}
                  disabled={policy.insurancePolicyType.name === PolicyTypes.UmbrellaExcess}
                  id={policy.id}
                  pageInfo={get(linkedItems, `${policy.id}.pageInfo`)}
                />
              )}

              {aiScanEnabled && (
                <AiScanIcon policy={policy} recordId={recordId} />
              )}

              <PolicyNameGrid
                companyId={companyId}
                id={policy.id}
                insuredId={insuredId}
                jobId={jobId}
                policy={policy}
                recordId={recordId}
              />
              <PolicyLimitsOptionsGrid limits={booleanProperties} />
              <PolicyDetailsGrid policy={policy} />
              <PolicyLimitsGrid alphaNumericProperties={alphaNumericProperties} limits={limits} policy={policy} />
              <StatusOptions
                insuredId={insuredId}
                jobId={jobId}
                policy={policy}
                recordId={recordId}
              />
            </Grid>
          );
        })}
      </Box>
    </Box>
  );
};

PolicyTable.propTypes = {
  companyId: PropTypes.number.isRequired,
  recordId: PropTypes.number.isRequired,
  insuredId: PropTypes.number.isRequired,
  jobId: PropTypes.number.isRequired,
  matchedPoliciesAndRequirements: PropTypes.arrayOf(PropTypes.shape({}))
};

export default PolicyTable;
